import App from "./app.tsx";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import "./index.css";
import { Provider } from "react-redux";
import { Analytics } from "@vercel/analytics/react";
import store from "./store/store.ts";
import { createContext, useContext, useEffect, useState } from "react";
import { BrowserRouter, useNavigate, useLocation } from "react-router-dom";
import ReactDOM from "react-dom/client";
import { AppProvider } from "./middleware.tsx";

type Language = "en" | "ar";
type Direction = "ltr" | "rtl";

interface LanguageContextValue {
  language: Language;
  direction: Direction;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextValue>({
  language: "en",
  direction: "ltr",
  setLanguage: () => {},
});

const LanguageMiddleware: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [language, setLanguage] = useState<Language>("en");
  const [direction, setDirection] = useState<Direction>("ltr");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const browserLang = navigator.language.split("-")[0] as Language;
    const supportedLanguages: Record<Language, Direction> = {
      en: "ltr",
      ar: "rtl",
    };

    const newLang = supportedLanguages[browserLang] ? browserLang : "en";

    setLanguage(newLang);
  }, [location.pathname, navigate]);

  const contextValue: LanguageContextValue = {
    language,
    direction,
    setLanguage: (newLang: Language) => {
      const supportedLanguages: Record<Language, Direction> = {
        en: "ltr",
        ar: "rtl",
      };
      const newDirection = supportedLanguages[newLang] || "ltr";
      setLanguage(newLang);
      setDirection(newDirection);
      document.documentElement.dir = newDirection;
      const currentPath = location.pathname.split("/").slice(2).join("/") || "";
      navigate(`/${newLang}/${currentPath}`, { replace: true });
    },
  };

  const theme = createTheme({
    direction,
  });

  return (
    <LanguageContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <Provider store={store}>
          <AppProvider>
            {children}
            <Analytics />
          </AppProvider>{" "}
          {/* Wrap AppProvider here */}
        </Provider>
      </ThemeProvider>
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextValue =>
  useContext(LanguageContext);

const Root: React.FC = () => {
  return (
    <BrowserRouter>
      <LanguageMiddleware>
        <App />
      </LanguageMiddleware>
    </BrowserRouter>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(<Root />);
